import { useCallback, useMemo } from "react"
import { defineMessages, useIntl } from "react-intl"
import { MAX_INPUT_LENGTH } from "../constants"

export const message = defineMessages({
    required: {
        id: "validator.required",
        defaultMessage: "this is required",
    },
    numberAndrequired: {
        id: "validator.number.required",
        defaultMessage: "Must be a number",
    },
    email: {
        id: "validator.invalid.email",
        defaultMessage: "invalid email format",
    },
    phone: {
        id: "validator.invalid.phone",
        defaultMessage: "invalid phone format",
    },
    filename: {
        id: "validator.invalid.filename",
        defaultMessage: "name can't contain '/'",
    },
    max: {
        id: "validator.invalid.max",
        defaultMessage: "should not more than {num} characters",
    },
    min: {
        id: "validator.invalid.min",
        defaultMessage: "should not less than {num} characters",
    },
})

const EMAIL_REGEX =
    /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/

export default function useValidator() {
    const intl = useIntl()

    const noAllSpace = useCallback(
        (str: string | number) => {
            if (typeof str === "string")
                return !!str.trim() || intl.formatMessage(message.required)
            return true
        },
        [intl],
    )
    const noSlashInFilename = useCallback(
        (str: string) =>
            !str.includes("/") || intl.formatMessage(message.filename),
        [intl],
    )

    const applyValidations = useCallback(
        (str: string, rules: Array<(str: string) => Boolean | string>) => {
            for (let i = 0; i < rules.length; i++) {
                const result = rules[i](str)
                if (typeof result === "string") {
                    return result
                }
            }
            return true
        },
        [],
    )
    const numberAndrequired = useMemo(
        () => ({
            required: intl.formatMessage(message.required),
            validate: (value: string) => {
                if (noAllSpace(value) !== true) return noAllSpace(value)
                if (isNaN(Number(value)))
                    return intl.formatMessage(message.numberAndrequired)
                return true
            },
        }),
        [intl, noAllSpace],
    )
    const required = useMemo(
        () => ({
            required: intl.formatMessage(message.required),
            validate: noAllSpace,
        }),
        [intl, noAllSpace],
    )

    const email = useMemo(
        () => ({
            ...required,
            pattern: {
                value: EMAIL_REGEX,
                message: intl.formatMessage(message.email),
            },
            maxLength: {
                value: MAX_INPUT_LENGTH,
                message: intl.formatMessage(message.max, {
                    num: MAX_INPUT_LENGTH,
                }),
            },
        }),
        [intl, required],
    )

    const emailNotRequired = useMemo(
        () => ({
            pattern: {
                value: EMAIL_REGEX,
                message: intl.formatMessage(message.email),
            },
        }),
        [intl],
    )

    const password = useMemo(
        () => ({
            ...required,
            minLength: {
                value: 6,
                message: intl.formatMessage(message.min, { num: 6 }),
            },
            maxLength: {
                value: MAX_INPUT_LENGTH,
                message: intl.formatMessage(message.max, {
                    num: MAX_INPUT_LENGTH,
                }),
            },
        }),
        [intl, required],
    )
    const maxInputLength = useMemo(
        () => ({
            ...required,
            maxLength: {
                value: MAX_INPUT_LENGTH,
                message: intl.formatMessage(message.max, {
                    num: MAX_INPUT_LENGTH,
                }),
            },
        }),
        [intl, required],
    )
    const phone = useMemo(
        () => ({
            ...required,
            maxLength: {
                value: 20,
                message: intl.formatMessage(message.max, { num: 20 }),
            },
            pattern: {
                value: /^\+?([0-9][- ]?)+[0-9]$/,
                message: intl.formatMessage(message.phone),
            },
        }),
        [intl, required],
    )
    const filename = useMemo(
        () => ({
            ...required,
            maxLength: {
                value: MAX_INPUT_LENGTH,
                message: intl.formatMessage(message.max, {
                    num: MAX_INPUT_LENGTH,
                }),
            },
            validate: (str: string) =>
                applyValidations(str, [noAllSpace, noSlashInFilename]),
        }),
        [intl, required, applyValidations, noAllSpace, noSlashInFilename],
    )

    return {
        email,
        filename,
        required,
        password,
        phone,
        emailNotRequired,
        maxInputLength,
        numberAndrequired,
    }
}
